import { AccountType, SecurityIOCFeedsRoutingStates, SecurityRulesStates } from '@logz-pkg/enums';
import { sessionStateService } from '@logz-pkg/frontend-services';
import { RedirectionStateObject } from '../types';
import { pickOsdParams, splitOsdResource, tanstackUrlSearch, uriSafe } from '../utils';
import {
  researchDashboardRouteName,
  researchRouteName,
  securityDynamicLookupCreateRouteName,
  securityDynamicLookupEditRouteName,
  securityEventManagementRouteName,
  securityLookupsListEditRouteName,
  securityLookupsListRouteName,
  securitySummaryRouteName,
  securityThreatsOverviewRouteName,
  securityThreatsRouteName,
} from 'states/security/security.routes.names';

const isSecuritySession = (): boolean =>
  sessionStateService.session.get()?.loggedInAccount?.type === AccountType.Security;

export const siemRedirections: RedirectionStateObject = {
  'dashboard.security': () => `/360/siem/summary`,

  [securitySummaryRouteName]: params => `/360/siem/summary${tanstackUrlSearch(pickOsdParams(params))}`,
  [securityThreatsRouteName]: () => `/360/siem/threats`,
  [securityThreatsOverviewRouteName]: params => `/360/siem/threats${tanstackUrlSearch(pickOsdParams(params))}`,

  [researchDashboardRouteName]: () => `/360/siem/dashboards`,
  [researchRouteName]: ({ resource, ...rest }) => {
    const { app, hash } = splitOsdResource(resource);
    const osd = pickOsdParams(rest);

    if (app === 'discover' || app === 'visualize') {
      return `/360/siem/osd${tanstackUrlSearch({ ...osd, app, hash })}`;
    }

    return `/360/siem/dashboards${tanstackUrlSearch({ ...osd, hash })}`;
  },

  [SecurityIOCFeedsRoutingStates.Table]: () => `/360/siem/feeds`,
  [SecurityIOCFeedsRoutingStates.New]: () => `/360/siem/feeds/new`,
  [SecurityIOCFeedsRoutingStates.Edit]: ({ id }) => `/360/siem/feeds/${uriSafe(id)}`,

  'dashboard.security.rules': () => `/360/siem/rules`,
  'dashboard.security.rules.definitions': params => `/360/siem/rules${tanstackUrlSearch(params)}`,
  [SecurityRulesStates.New]: () => `/360/siem/rules/new`,
  [SecurityRulesStates.Edit]: ({ id }) => `/360/siem/rules/${uriSafe(id)}`,
  [SecurityRulesStates.Duplicate]: ({ id }) => `/360/siem/rules/new${tanstackUrlSearch({ duplicateFrom: String(id) })}`,

  [securityLookupsListRouteName]: () => `/360/siem/lookups`,
  [securityLookupsListEditRouteName]: ({ lookupId }) => `/360/siem/lookups/${uriSafe(lookupId)}`,
  [securityDynamicLookupCreateRouteName]: () => `/360/siem/lookups/dynamic/new`,
  [securityDynamicLookupEditRouteName]: ({ lookupId }) => `/360/siem/lookups/${uriSafe(lookupId)}`,

  [securityEventManagementRouteName]: ({ search, statuses }) => {
    const status = Array.isArray(statuses) && statuses.length ? statuses[0] : undefined;

    return `/360/siem/event-management${tanstackUrlSearch({ search, status })}`;
  },

  'dashboard.settings.drilldowns': () => `/360/siem/drilldowns`,

  'dashboard.scheduled-reports': params =>
    isSecuritySession()
      ? `/360/siem/reports${tanstackUrlSearch(params)}`
      : `/360/settings/scheduled-reports${tanstackUrlSearch(params)}`,
  'dashboard.scheduled-reports-new': params =>
    isSecuritySession()
      ? `/360/siem/reports/new${tanstackUrlSearch(params)}`
      : `/360/settings/scheduled-reports/new${tanstackUrlSearch(params)}`,
  'dashboard.scheduled-reports-edit': ({ id, ...rest }) =>
    isSecuritySession()
      ? `/360/siem/reports/${uriSafe(id)}${tanstackUrlSearch(rest)}`
      : `/360/settings/scheduled-reports/${uriSafe(id)}${tanstackUrlSearch(rest)}`,
};
